import { type PrimaryProductKey } from '../../common/constants/product-keys';

/**
 * Inputs needed to derive the SAIN intent-detection GraphQL variables.
 */
export type BuildIntentVariablesInput = {
	cloudId: string;
	primaryProduct: PrimaryProductKey | undefined;
	/** From `bootstrap.analyticsAttributes.activationId`. */
	activationId: string | undefined;
	locale: string;
	orgId: string | undefined;
	query: string;
};

/**
 * GraphQL variables shape consumed by `getQueryIntent`.
 */
export type IntentDetectionVariables = {
	query: string;
	siteId: string;
	locale: string;
	orgId: string;
	workspaceId: string;
	cloudId: string;
	product: string;
};

/**
 * Pure derivation of intent-detection GraphQL variables from bootstrap state.
 * Shared by `useQueryIntent` and the Quick Find warm-up preload so both call
 * sites agree on the variables passed to `getQueryIntent`.
 *
 * Rules:
 *
 *  - `siteARI = ari:cloud:{primaryProduct}::site/{cloudId}`
 *  - When `primaryProduct === 'jira'`, override to the jira-software ARI
 *    (`ari:cloud:jira-software::site/{cloudId}`) — intent detection targets
 *    `jira_web` so `jira` must map to `jira-software`.
 *  - `workspaceId = ari:cloud:{primaryProduct}:{cloudId}:workspace/{activationId}`
 *    when `activationId` is set, else `''`.
 *  - `orgId` defaults to `''`.
 */
export const buildIntentDetectionVariables = ({
	cloudId,
	primaryProduct,
	activationId,
	locale,
	orgId,
	query,
}: BuildIntentVariablesInput): IntentDetectionVariables => {
	const product = primaryProduct || '';
	const siteId =
		primaryProduct === 'jira'
			? `ari:cloud:jira-software::site/${cloudId}`
			: `ari:cloud:${product}::site/${cloudId}`;
	const workspaceId = activationId
		? `ari:cloud:${product}:${cloudId}:workspace/${activationId}`
		: '';

	return {
		query,
		siteId,
		locale,
		orgId: orgId ?? '',
		workspaceId,
		cloudId,
		product,
	};
};
